/*======================================================================================================================
  * Bluebell: src/app/state/mind-maps/mind-maps.reducer.ts
  * Last Edited: 11/08/2025
  * Copyright 2024-2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { programmeProjectSelectionUnapplied } from '@state/account/account.actions';
import {
  clearMindMap,
  createMindMapSuccess,
  deleteMindMapSuccess,
  loadMindMaps,
  loadMindMapsFailure,
  loadMindMapsSuccess,
  saveMindMap,
  saveMindMapSuccess,
  selectMindMapSuccess,
  updateMindMapSuccess,
} from '@state/mind-maps/mind-maps.actions';
import { MindMap, NodeData } from '@state/mind-maps/mind-maps.interfaces';

export interface State extends EntityState<MindMap> {
  mindMapLatest: NodeData[];
  mindMapSelected: MindMap|null;
  loading: boolean;
  saving: boolean;
  lastSaved: string|null;
}

export const adapter: EntityAdapter<MindMap> = createEntityAdapter<MindMap>({
  selectId: (mindMap: MindMap) => mindMap.uuid,
});

export const initialState: State = adapter.getInitialState<State>({
  mindMapLatest: [],
  mindMapSelected: null,
  loading: false,
  saving: false,
  lastSaved: null,
});

export const reducer = createReducer(
  initialState,

  on(programmeProjectSelectionUnapplied, () => {
    return adapter.removeAll({ ...initialState });
  }),

  on(loadMindMaps, (state) => {
    return adapter.removeAll({
      ...state,
      loading: true,
    });
  }),

  on(loadMindMapsSuccess, (state, { mindMaps }) => {
    return adapter.setAll(mindMaps, {
      ...state,
      loading: false,
      lastSaved: null,
    });
  }),

  on(loadMindMapsFailure, (state) => {
    return {
      ...state,
      loading: false,
    };
  }),

  on(createMindMapSuccess, (state, { mindMap }) => {
    return adapter.addOne(mindMap, {
      ...state,
      mindMapSelected: mindMap,
    });
  }),

  on(deleteMindMapSuccess, (state, { uuid }) => {
    return adapter.removeOne(uuid, state);
  }),

  on(saveMindMap, (state) => {
    return {
      ...state,
      saving: true,
    };
  }),

  on(updateMindMapSuccess, saveMindMapSuccess, (state, { mindMap }) => {
    return adapter.upsertOne(mindMap, {
      ...state,
      mindMapSelected: mindMap,
      loading: false,
      saving: false,
      lastSaved: new Date().toISOString(),
    });
  }),

  on(selectMindMapSuccess, (state, { mindMap }) => {
    return adapter.upsertOne(mindMap, {
      ...state,
      mindMapSelected: mindMap,
      mindMapLatest: mindMap.content,
    });

  }),

  on(clearMindMap, (state) => {
    return {
      ...state,
      mindMapLatest: [],
      mindMapSelected: null,
      lastSaved: null,
    };
  }),
);

export const {
  selectIds,
  selectEntities,
  selectAll,
  selectTotal,
} = adapter.getSelectors();

export const featureKey = 'mindMaps';
export const getLatestMindMapData = (state: State) => state.mindMapLatest;
export const getSelectedMindMap = (state: State) => state.mindMapSelected;
export const getMindMapsLoading = (state: State) => state.loading;
export const getLastSaved = (state: State) => state.lastSaved;
export const getSaving = (state: State) => state.saving;
